import { useState, useEffect,useCallback } from "react"
import { LOADING_MESSAGES, CONFIRMATION_MESSAGES } from "common/Utility"
import type { errorObject, Tag } from "types/components/IAITagGenerationWizard"

export function useLoadingMessages(isGeneratingTags: boolean, isCreatingTags: boolean) {
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const [confirmMessageIndex, setConfirmMessageIndex] = useState(0)

  useEffect(() => {
    if (isGeneratingTags || isCreatingTags) {
      const interval = setInterval(() => {
        if (isGeneratingTags) {
          setLoadingMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length)
        } else if (isCreatingTags) {
          setConfirmMessageIndex((prev) => (prev + 1) % CONFIRMATION_MESSAGES.length)
        }
      }, 2500)
      return () => clearInterval(interval)
    }
  }, [isGeneratingTags, isCreatingTags])

  return {
    loadingMessageIndex,
    confirmMessageIndex,
    currentLoadingMessage: LOADING_MESSAGES[loadingMessageIndex],
    currentConfirmationMessage: CONFIRMATION_MESSAGES[confirmMessageIndex],
  }
}

export function useTagGeneration(
  generateTagsWithAI: (payload: { prompt: string; numTags: number }) => Promise<
    Array<{ name: string; description: string }>
  >,
  resetAiGeneratedTags?: () => void,
  aiGeneratedTags?: any,
  DataTagList?: Array<any>,
  isGeneratingTags?: boolean,
  aiGeneratedErrors?: errorObject | null,
  rephraseWithAI?: (payload: { tagName: string; prevDescription: string }) => Promise<{ newDescription: string }>,
  isRephrasing?: boolean,
  rephrasedDataTag?: any,
) {
  const [generatedTags, setGeneratedTags] = useState<Tag[]>([])
  const [requestedTagCount, setRequestedTagCount] = useState<number>(20)
  const [customPrompt, setCustomPrompt] = useState("")
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null)
  const [createCustomPrompt, setCreateCustomPrompt] = useState(false)
  const [searchText, setSearchText] = useState<string>("")
  const [viewMode, setViewMode] = useState<"list" | "grid">("list")
  const [currentRephrasingTagIndex, setCurrentRephrasingTagIndex] = useState<number | null>(null)
  const [pendingRephrase, setPendingRephrase] = useState(false)

  useEffect(() => {
    if (aiGeneratedTags && Array.isArray(aiGeneratedTags)) {
      const existingTags = new Set(DataTagList?.map((tag) => tag.tagName.toLowerCase()) || [])
      const generatedTagsWithDuplicates = new Set<string>()

      const processedTags = aiGeneratedTags.map((tag) => {
        let newName = tag.name
        const originalName = tag.name
        let isDuplicate = false
        let counter = 1
        let potentialNameLower = newName.toLowerCase()

        while (existingTags.has(potentialNameLower) || generatedTagsWithDuplicates.has(potentialNameLower)) {
          isDuplicate = true
          newName = `${originalName}-(${counter})`
          potentialNameLower = newName.toLowerCase()
          counter++
        }

        generatedTagsWithDuplicates.add(potentialNameLower)

        return {
          ...tag,
          name: newName,
          originalName: originalName,
          selected: true,
          isDuplicate: isDuplicate,
        }
      })

      setGeneratedTags(processedTags)
    }
  }, [aiGeneratedTags, DataTagList])

  useEffect(() => {
    if (isRephrasing && !pendingRephrase) {
      setPendingRephrase(true)
    }

    if (!isRephrasing && pendingRephrase) {
      if (rephrasedDataTag && currentRephrasingTagIndex !== null) {
        setGeneratedTags((prev) =>
          prev.map((tag, i) =>
            i === currentRephrasingTagIndex
              ? {
                  ...tag,
                  description: rephrasedDataTag.newDescription,
                  isRephrasing: false,
                  originalDescription: tag.originalDescription || tag.description,
                }
              : tag,
          ),
        )
      } else {
        setGeneratedTags((prev) =>
          prev.map((tag, i) =>
            i === currentRephrasingTagIndex
              ? {
                  ...tag,
                  isRephrasing: false,
                }
              : tag,
          ),
        )
      }

      setCurrentRephrasingTagIndex(null)
      setPendingRephrase(false)
    }
  }, [isRephrasing, pendingRephrase, rephrasedDataTag, currentRephrasingTagIndex])

  const handleGenerateTags = useCallback(async () => {
    if (!customPrompt.trim()) return

    if (resetAiGeneratedTags) {
      resetAiGeneratedTags()
      setGeneratedTags([])
    }

    try {
      const payload = {
        prompt: customPrompt,
        numTags: requestedTagCount,
      }
      generateTagsWithAI(payload)
    } catch (error) {
      console.error("Error generating tags:", error)
    }
  }, [customPrompt, requestedTagCount, resetAiGeneratedTags, generateTagsWithAI])

  const handleTagSelection = useCallback((index: number) => {
    setGeneratedTags((prev) => prev.map((tag, i) => (i === index ? { ...tag, selected: !tag.selected } : tag)))
  }, [])

  const handleSelectAll = useCallback(() => {
    setGeneratedTags((prev) => prev.map((tag) => ({ ...tag, selected: true })))
  }, [])

  const handleDeselectAll = useCallback(() => {
    setGeneratedTags((prev) => prev.map((tag) => ({ ...tag, selected: false })))
  }, [])

  const getFilteredTags = useCallback(() => {
    return generatedTags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(searchText.toLowerCase()) ||
        tag.description.toLowerCase().includes(searchText.toLowerCase()),
    )
  }, [generatedTags, searchText])

  const handleRephraseDescription = useCallback(
    async (index: number) => {
      if (!rephraseWithAI || isRephrasing) return
      const isAnyTagRephrasing = generatedTags.some((tag) => tag.isRephrasing)
      if (isAnyTagRephrasing) return

      const originalTag = generatedTags[index]
      setGeneratedTags((prev) =>
        prev.map((tag, i) =>
          i === index
            ? {
                ...tag,
                isRephrasing: true,
                originalDescription: tag.originalDescription || tag.description, // Store original description for potential revert
              }
            : tag,
        ),
      )
      setCurrentRephrasingTagIndex(index)

      try {
        await rephraseWithAI({
          tagName: originalTag.name,
          prevDescription: originalTag.description,
        })
      } catch (error) {
        console.error("Error rephrasing description:", error)
        setGeneratedTags((prev) =>
          prev.map((tag, i) =>
            i === index
              ? {
                  ...tag,
                  isRephrasing: false,
                }
              : tag,
          ),
        )
        setCurrentRephrasingTagIndex(null)
        setPendingRephrase(false)
      }
    },
    [rephraseWithAI, isRephrasing, generatedTags],
  )

  const handleRevertDescription = useCallback((index: number) => {
    setGeneratedTags((prev) =>
      prev.map((tag, i) =>
        i === index && tag.originalDescription
          ? {
              ...tag,
              description: tag.originalDescription,
              originalDescription: undefined,
            }
          : tag,
      ),
    )
  }, [])

  const handleUpdateDescription = useCallback((index: number, newDescription: string) => {
    setGeneratedTags((prev) =>
      prev.map((tag, i) =>
        i === index
          ? {
              ...tag,
              description: newDescription,
            }
          : tag,
      ),
    )
  }, [])

  const resetTagGeneration = useCallback(() => {
    setGeneratedTags([])
    setRequestedTagCount(20)
    setCustomPrompt("")
    setSelectedPromptId(null)
    setCreateCustomPrompt(false)
    setSearchText("")
    setViewMode("list")
    setCurrentRephrasingTagIndex(null)
    setPendingRephrase(false)
  }, [])

  return {
    generatedTags,
    setGeneratedTags,
    requestedTagCount,
    setRequestedTagCount,
    customPrompt,
    setCustomPrompt,
    selectedPromptId,
    setSelectedPromptId,
    createCustomPrompt,
    setCreateCustomPrompt,
    searchText,
    setSearchText,
    viewMode,
    setViewMode,
    handleGenerateTags,
    handleTagSelection,
    handleSelectAll,
    handleDeselectAll,
    getFilteredTags,
    resetTagGeneration,
    handleRephraseDescription,
    handleRevertDescription,
    handleUpdateDescription,
    currentRephrasingTagIndex,
  }
}

export function useErrorHandling(
  aiGeneratedErrors: errorObject | null,
  bulkCreateErrors: errorObject | null,
) {
  const [errorObj, setErrorObj] = useState<errorObject | null>(null)

  useEffect(() => {
    if (aiGeneratedErrors) {
      setErrorObj(aiGeneratedErrors)
    } else if (bulkCreateErrors) {
      setErrorObj(bulkCreateErrors)
    } else {
      setErrorObj(null)
    }
  }, [aiGeneratedErrors, bulkCreateErrors])

  return { errorObj }
}

export function useWizardSteps(
  isGeneratingTags: boolean,
  isCreatingTags: boolean,
  generatedTags: Array<any>,
  aiGeneratedErrors?: errorObject | null,
  bulkCreateErrors?: errorObject | null,
) {
  const [currentStep, setCurrentStep] = useState(0)
  const [saveInitiated, setSaveInitiated] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null)


  useEffect(() => {
    if (currentStep === 2 && !isGeneratingTags) {
      if (generatedTags.length > 0 && !aiGeneratedErrors) {
        setCurrentStep(3)
        setHasUnsavedChanges(true)
        setLastErrorMessage(null)
      } else if (aiGeneratedErrors) {

        setCurrentStep(1)
        setLastErrorMessage(aiGeneratedErrors.message)
      }
    }

    if (currentStep === 4 && saveInitiated && !isCreatingTags) {
      if (bulkCreateErrors) {
        setCurrentStep(3)
        setSaveInitiated(false)
        setLastErrorMessage(bulkCreateErrors.message)
      } else {
        setHasUnsavedChanges(false)
        setLastErrorMessage(null)
      }
    }
  }, [
    isGeneratingTags,
    isCreatingTags,
    currentStep,
    generatedTags.length,
    saveInitiated,
    aiGeneratedErrors,
    bulkCreateErrors,
  ])

  const nextStep = () => {
    setCurrentStep(currentStep + 1)
  }

  const prevStep = () => {
    if (currentStep === 3) {
      setCurrentStep(1) 
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const resetSteps = () => {
    setCurrentStep(0)
    setSaveInitiated(false)
    setHasUnsavedChanges(false)
    setLastErrorMessage(null)
  }

  return {
    currentStep,
    setCurrentStep,
    saveInitiated,
    setSaveInitiated,
    hasUnsavedChanges,
    lastErrorMessage,
    nextStep,
    prevStep,
    resetSteps,
  }
}
